import { Typography } from '@mui/material'
import dayjs, { Dayjs } from 'dayjs'
import { observer } from 'mobx-react-lite'
import { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { AppBox } from 'src/component/common/analog/molecule/AppBox'
import { AppButton } from 'src/component/common/analog/molecule/button/AppButton'
import { AppPaper } from 'src/component/common/analog/molecule/AppPaper'
import { Caption } from 'src/feature/localization/component/atom/Caption'
import { useLocalizationStore } from 'src/feature/localization/store/localization.store'
import { appRoutePath } from 'src/feature/router/util/app-route-path'
import { Palette } from 'src/feature/theme/model/Palette'
import { PageMetrics } from '../molecules/PageMetrics'
import { TeamLearningChart } from '../organisms/TeamLearningChart'
import { TeamLearningHighlight } from '../organisms/TeamLearningHighlight'
import { useMyTeamStore } from '../../store/my-team.store'

export type CourseInsightsProps = {
  fromDate: Dayjs | null
  toDate: Dayjs | null
  selectedTeam: string[]
}

export const CourseInsights = observer(({ fromDate, toDate, selectedTeam }: CourseInsightsProps) => {
  const myTeamStore = useMyTeamStore()
  const { translation } = useLocalizationStore()
  const navigate = useNavigate()
  const { isLoading: development } = myTeamStore.getGenericFieldsState('getCourseDevelopment')
  const { isLoading: highlights } = myTeamStore.getGenericFieldsState('getCourseHighlights')

  const pageTranslation = translation.pages.myTeam
  const isLoading = highlights || development

  const metrics = [
    {
      label: pageTranslation.metricsHeader.coursesGiven(),
      value: myTeamStore.courseDevelopment?.totalAssigned || '-',
    },
    {
      label: pageTranslation.metricsHeader.coursesCompleted(),
      value: myTeamStore.courseDevelopment?.totalCompleted || '-',
    },
    {
      label: pageTranslation.metricsHeader.inProgressCourses(),
      value: myTeamStore.courseDevelopment?.totalInProgress || '-',
    },
    {
      label: pageTranslation.metricsHeader.favoriteCourses(),
      value: myTeamStore.courseDevelopment?.totalWishlisted || '-',
      icon: 'FavoriteRoundedIcon',
      iconColor: 'secondary.main',
    },
    {
      label: pageTranslation.metricsHeader.assignedCompletionRate(),
      value: myTeamStore.courseDevelopment?.uniqueAssignedCompletionRate ? `${myTeamStore.courseDevelopment.uniqueAssignedCompletionRate}%` : '-',
      valueColor: true,
      tootlTipDesc: pageTranslation.metricsHeader.assignedCompletionRateTooltip(),
    },
    {
      label: pageTranslation.metricsHeader.wishlistedCompletionRate(),
      value: myTeamStore.courseDevelopment?.uniqueWishlistedCompletionRate ? `${myTeamStore.courseDevelopment.uniqueWishlistedCompletionRate}%` : '-',
      valueColor: true,
      tootlTipDesc: pageTranslation.metricsHeader.wishlistedCompletionRateTooltip(),
    },
    {
      label: pageTranslation.metricsHeader.highestRated(),
      value: myTeamStore.courseDevelopment?.highPerformanceTeam || '-',
      icon: 'ArrowUpwardRoundedIcon',
      iconColor: Palette.OLIVE_GREEN,
    },
    {
      label: pageTranslation.metricsHeader.lowestRated(),
      value: myTeamStore.courseDevelopment?.lowPerformanceTeam || '-',
      icon: 'ArrowDownwardRoundedIcon',
      iconColor: Palette.DANGER,
    },
  ]
  
  const getHighlightInfo = (highlightName: string) => {
    switch (highlightName) {
    case 'popular':
      return {
        displayName: pageTranslation.metricsHeader.mostPopularCourses(),
        emptyMessage: pageTranslation.metricsHeader.noPopularCoursesMessage(),
      }
    case 'assigned':
      return {
        displayName: pageTranslation.metricsHeader.mostAssignedCourses(),
        emptyMessage: pageTranslation.metricsHeader.noAssignedCoursesMessage(),
      }
    case 'completed':
      return {
        displayName: pageTranslation.metricsHeader.mostCompletedCourses(),
        emptyMessage: pageTranslation.metricsHeader.noCompletedCoursesMessage(),
      }
    case 'wishlisted':
      return {
        displayName: pageTranslation.metricsHeader.mostWishlistedCourses(),
        emptyMessage: pageTranslation.metricsHeader.noWishlistedCoursesMessage(),
      }
    default:
      return {
        displayName: highlightName,
        emptyMessage: pageTranslation.metricsHeader.noCourseDataMessage(),
      }
    }
  }

  useEffect(() => {
    if (selectedTeam.length === 0) {
      return
    }

    const formattedFromDate = fromDate ? dayjs(fromDate).toISOString() : ''
    const formattedToDate = toDate ? dayjs(toDate).toISOString() : ''

    myTeamStore.getCourseDevelopment(formattedFromDate, formattedToDate, selectedTeam)
    myTeamStore.getCourseHighlights(formattedFromDate, formattedToDate, selectedTeam)
  }, [fromDate, toDate, selectedTeam, myTeamStore])

  return (
    <AppBox sx={styles.root}>
      <AppBox sx={{ width: '100%', flexDirection: { xs: 'column', md: 'row' }, gap: 15 }}>
        <PageMetrics
          sx={{
            width: 'auto',
            display: 'grid',
            gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
            gap: 15,
          }}
          metrics={metrics}
          isLoading={isLoading}
        />
        <TeamLearningChart
          assignedData={myTeamStore.courseDevelopment?.assigned || []}
          wishlistedData={myTeamStore.courseDevelopment?.wishlisted || []}
          isLoading={isLoading}
          maxWidth="70%"
          assignedTitle={pageTranslation.metricsHeader.coursesGiven()}
          wishlistedTitle={pageTranslation.metricsHeader.favoriteCourses()}
        />
      </AppBox>

      {myTeamStore.courseHighlights && myTeamStore.courseHighlights.length > 0 && (
        <AppBox sx={{ flexDirection: 'column', gap: 15 }}>
          {myTeamStore.courseHighlights.map((courseHighlights) => {
            const highlightInfo = getHighlightInfo(courseHighlights.highlightName)
            
            return courseHighlights.courseInfo && courseHighlights.courseInfo.length > 0 ? (
              <TeamLearningHighlight
                key={courseHighlights.highlightName}
                courseHighlights={courseHighlights}
                isLoading={isLoading}
                highlightDisplayName={highlightInfo.displayName}
              />
            ) : (
              <AppPaper key={courseHighlights.highlightName} sx={styles.emptyState}>
                <Typography variant="h4" fontWeight={600}>
                  {highlightInfo.displayName}
                </Typography>
                <Caption sx={styles.emptyStateText}>
                  {highlightInfo.emptyMessage}
                </Caption>
                <AppButton
                  fontSize={14}
                  onClick={() => navigate(appRoutePath.learning)}
                  variant="contained"
                  sx={{ marginTop: 'auto', fontWeight: 500 }}
                >
                  {pageTranslation.metricsHeader.exploreCourses()}
                </AppButton>
              </AppPaper>
            )
          })}
        </AppBox>
      )}
    </AppBox>
  )
})

const styles = {
  root: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    gap: 15,
  },
  emptyState: {
    borderRadius: '10px',
    width: '30em',
    height: '15em',
    gap: 10,
    padding: '1rem',
    display: 'flex',
    flexDirection: 'column',
  },
  emptyStateText: {
    color: 'text.secondary',
    fontSize: 11,
  },
}
